import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Sparkles, RotateCcw, Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import '../styles/design-system.css';

const quizQuestions = [
  {
    id: 1,
    question: "Como você se sente em relação às suas finanças atualmente?",
    options: [
      { text: "Tranquilo(a) e organizado(a)", points: { A: 3, B: 0, C: 0 } },
      { text: "Preocupado(a), mas buscando melhorar", points: { A: 1, B: 2, C: 0 } },
      { text: "Ansioso(a) e sem controle", points: { A: 0, B: 1, C: 2 } },
      { text: "Nunca parei para pensar nisso", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 2,
    question: "Qual sua relação com o dízimo e ofertas?",
    options: [
      { text: "Dizimo fielmente e oferto com alegria", points: { A: 3, B: 0, C: 0 } },
      { text: "Dizimo quando sobra dinheiro", points: { A: 0, B: 2, C: 1 } },
      { text: "Tenho dificuldade em separar para Deus", points: { A: 0, B: 1, C: 2 } },
      { text: "Não pratico o dízimo", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 3,
    question: "Você tem reserva financeira para emergências?",
    options: [
      { text: "Sim, para pelo menos 6 meses", points: { A: 3, B: 0, C: 0 } },
      { text: "Tenho algo guardado, mas pouco", points: { A: 1, B: 2, C: 0 } },
      { text: "Estou tentando começar a guardar", points: { A: 0, B: 2, C: 1 } },
      { text: "Não consigo guardar nada", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 4,
    question: "Como você toma decisões financeiras importantes?",
    options: [
      { text: "Oro, planejo e consulto a Palavra", points: { A: 3, B: 0, C: 0 } },
      { text: "Penso um pouco e decido", points: { A: 1, B: 2, C: 0 } },
      { text: "Decido por impulso na maioria das vezes", points: { A: 0, B: 1, C: 2 } },
      { text: "Evito pensar em finanças", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 5,
    question: "Qual é sua visão sobre prosperidade?",
    options: [
      { text: "É uma bênção de Deus para abençoar outros", points: { A: 3, B: 0, C: 0 } },
      { text: "Quero ter para viver bem", points: { A: 1, B: 2, C: 0 } },
      { text: "Dinheiro é a raiz de todos os males", points: { A: 0, B: 1, C: 2 } },
      { text: "Nunca vou conseguir prosperar", points: { A: 0, B: 0, C: 3 } }
    ]
  }
];

const profiles = {
  A: {
    title: "Mordomo Fiel",
    emoji: "👑",
    color: "var(--accent-green-200)",
    description: "Parabéns! Você demonstra maturidade financeira e alinhamento com os princípios bíblicos. Continue crescendo e ajudando outros a alcançarem esse nível!",
    tip: "Continue investindo em conhecimento e considere mentorear outras pessoas.",
    cta: "Potencialize seus resultados com mentoria avançada"
  },
  B: {
    title: "Em Transformação",
    emoji: "🌱",
    color: "var(--accent-blue-200)",
    description: "Você está no caminho certo! Tem consciência da importância das finanças e está buscando melhorar. Com orientação adequada, pode alcançar a excelência.",
    tip: "Estabeleça metas claras e busque conhecimento sobre finanças bíblicas.",
    cta: "Acelere sua transformação com consultoria personalizada"
  },
  C: {
    title: "Início da Jornada",
    emoji: "🌟",
    color: "var(--accent-pink-200)",
    description: "Este é o momento perfeito para uma mudança! Reconhecer a necessidade de transformação é o primeiro passo. Você tem potencial para uma vida financeira próspera e abençoada.",
    tip: "Comece com pequenos passos: organize suas contas e busque orientação.",
    cta: "Inicie sua transformação com acompanhamento especializado"
  }
};

const FinancialQuiz = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const calculateResult = () => {
    const totals = { A: 0, B: 0, C: 0 };
    answers.forEach(answer => {
      Object.keys(answer.points).forEach(key => {
        totals[key] += answer.points[key];
      });
    });
    
    const maxKey = Object.keys(totals).reduce((a, b) => totals[a] > totals[b] ? a : b);
    return profiles[maxKey];
  };

  const handleAnswer = (option) => {
    const newAnswers = [...answers, option];
    setAnswers(newAnswers);

    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setEmailSubmitted(false);
    setEmail('');
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('E-book enviado para seu e-mail!');
      setEmailSubmitted(true);
    }
  };

  const result = showResult ? calculateResult() : null;
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <>
      {/* CTA Banner */}
      <section 
        className="py-16 md:py-20"
        style={{ background: 'var(--alternate-gradient-hero-warm)' }}
      >
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: 'var(--accent-gold-200)', border: '1px solid var(--accent-gold-400)' }}
            >
              <Sparkles size={18} style={{ color: 'var(--accent-gold-400)' }} />
              <span className="mono-text" style={{ color: 'var(--text-secondary)' }}>Quiz Interativo</span>
            </div>
            
            <h2 className="heading-hero mb-4">
              Descubra seu Perfil Financeiro Bíblico
            </h2>
            <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
              Responda 5 perguntas rápidas e descubra como você se relaciona com as finanças 
              à luz dos princípios da Palavra de Deus.
            </p>
            
            <motion.button
              onClick={() => setIsOpen(true)}
              className="btn-primary text-lg px-8 py-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Fazer o Quiz Gratuito
              <ChevronRight size={20} className="ml-2" />
            </motion.button>
            
            <p className="caption mt-4" style={{ color: 'var(--text-muted)' }}>
              ✓ 100% gratuito ✓ Resultado imediato ✓ E-book de presente
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quiz Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.6)' }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
              style={{ background: 'var(--bg-card)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {!showResult ? (
                <>
                  {/* Quiz Header */}
                  <div className="p-6 pb-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="mono-text">
                        Pergunta {currentQuestion + 1} de {quizQuestions.length}
                      </span>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-light)' }}>
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: 'var(--text-primary)' }}
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>

                  {/* Question */}
                  <div className="px-6 pb-6">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentQuestion}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <h3 className="heading-2 mb-6">
                          {quizQuestions[currentQuestion].question}
                        </h3>
                        
                        <div className="space-y-3">
                          {quizQuestions[currentQuestion].options.map((option, index) => (
                            <motion.button
                              key={index}
                              onClick={() => handleAnswer(option)}
                              className="w-full p-4 rounded-xl text-left transition-all duration-200"
                              style={{ 
                                background: 'var(--bg-section)',
                                border: '2px solid transparent'
                              }}
                              whileHover={{ 
                                scale: 1.02,
                                background: 'var(--accent-purple-200)',
                                borderColor: 'var(--accent-purple-400)'
                              }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <span className="body-medium">{option.text}</span>
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Navigation */}
                  {currentQuestion > 0 && (
                    <div className="px-6 pb-6">
                      <button
                        onClick={handleBack}
                        className="btn-secondary flex items-center gap-2"
                      >
                        <ChevronLeft size={16} />
                        Voltar
                      </button>
                    </div>
                  )}
                </>
              ) : (
                /* Result */
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-6"
                >
                  <div 
                    className="text-center p-6 rounded-xl mb-6"
                    style={{ background: result.color }}
                  >
                    <span className="text-5xl mb-4 block">{result.emoji}</span>
                    <h3 className="heading-1 mb-2">{result.title}</h3>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                      {result.description}
                    </p>
                  </div>

                  <div className="voice-card accent-grey mb-6">
                    <p className="body-small">
                      <strong>💡 Dica:</strong> {result.tip}
                    </p>
                  </div>

                  {!emailSubmitted ? (
                    <form onSubmit={handleEmailSubmit} className="mb-6">
                      <p className="body-small mb-3">
                        🎁 Receba o E-book "7 Princípios Bíblicos da Prosperidade" grátis:
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Seu melhor e-mail"
                          required
                          className="flex-1 px-4 py-2 rounded-full text-sm"
                          style={{ 
                            background: 'var(--bg-section)', 
                            border: '1px solid var(--border-input)'
                          }}
                        />
                        <button type="submit" className="btn-primary">
                          <Download size={16} />
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="voice-card accent-green mb-6 text-center">
                      <p className="body-small">✅ E-book enviado! Verifique seu e-mail.</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={resetQuiz}
                      className="btn-secondary flex-1 flex items-center justify-center gap-2"
                    >
                      <RotateCcw size={16} />
                      Refazer
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success('Link copiado!');
                      }}
                      className="btn-secondary flex-1 flex items-center justify-center gap-2"
                    >
                      <Share2 size={16} />
                      Compartilhar
                    </button>
                  </div>

                  <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--border-light)' }}>
                    <a
                      href="#contact"
                      onClick={() => setIsOpen(false)}
                      className="btn-primary w-full"
                    >
                      {result.cta}
                    </a>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FinancialQuiz;
